import { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Banknote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const CARDS = [["total_earned", "Total earned"], ["tips_earned", "Tips earned"], ["available", "Available — money on hand in account"], ["pending", "Pending (24h)"], ["deposits_made", "Deposits made"]];

const Card = ({ label, value, testId }) => (
  <div className="lux-card p-4 sm:p-5">
    <p className="lux-eyebrow !text-[0.6rem]">{label}</p>
    <p data-testid={testId} className="gold-text mt-2 font-serif text-2xl font-semibold sm:text-3xl">{money(value)}</p>
  </div>
);

const Withdraw = ({ summary, onDone }) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await api.post("/driver/withdrawals", { amount: Number(amount) }); toast.success("Withdrawal requested"); setAmount(""); onDone(); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="lux-card space-y-3 p-5 sm:p-6" data-testid="withdraw-form">
      <p className="lux-eyebrow">Withdraw to bank {summary.bank_last4 ? `••${summary.bank_last4}` : ""}</p>
      <div className="flex gap-2">
        <input type="number" min="0.01" step="0.01" required data-testid="withdraw-amount-input" className="lux-input min-w-0 flex-1" placeholder={`Up to ${money(summary.available)}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
        <button type="submit" disabled={busy || !amount} data-testid="withdraw-submit-button" className="btn-gold !px-5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />} Withdraw</button>
      </div>
      {error && <p data-testid="withdraw-error" className="text-xs font-medium text-amber-500">{error}</p>}
    </form>
  );
};

const History = () => {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/driver/earnings/history?period=${period}`).then(({ data }) => setData(data)); }, [period]);
  return (
    <div className="lux-card min-w-0 p-4 sm:p-6" data-testid="earnings-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-amber-500/20 p-1">
          {["week", "month", "year"].map((p) => (
            <button key={p} data-testid={`earnings-tab-${p}`} onClick={() => setPeriod(p)} className={`rounded-full px-4 py-1.5 text-sm capitalize transition-colors ${period === p ? "bg-amber-400 text-black" : "text-zinc-300"}`}>{p}</button>
          ))}
        </div>
        {data && <p className="text-sm text-zinc-400">This {period}: <span data-testid="earnings-period-total" className="font-semibold text-amber-100">{money(data.total)}</span></p>}
      </div>
      {data && (
        <>
          <div className="mt-5 h-56 w-full" data-testid={`earnings-chart-${period}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart} margin={{ left: -18, right: 4 }}>
                <XAxis dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#121216", border: "1px solid rgba(251,191,36,.3)" }} />
                <Bar dataKey="amount" fill="#d4af37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 divide-y divide-white/5" data-testid="earning-lines">
            {data.lines.length === 0 && <p className="py-3 text-sm text-zinc-500">No earnings in this period yet.</p>}
            {data.lines.map((l) => (
              <div key={l.id} data-testid={`earning-line-${l.id}`} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0"><p className="truncate text-zinc-200">{l.label}</p><p className="text-xs text-zinc-500">{fmtDate(l.created_at)} · <span className={l.status === "pending" ? "text-amber-400" : "text-emerald-400"}>{l.status === "pending" ? "Pending" : "Available"}</span></p></div>
                <span className="font-mono text-amber-100">{money(l.amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const DriverRecord = () => {
  const [r, setR] = useState(null);
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/driver/refusals").then(({ data }) => setR(data)); api.get("/driver/speeding").then(({ data }) => setS(data)); }, []);
  if (!r || !s) return null;
  const stat = (label, v, id) => <div><p className="lux-eyebrow !text-[0.6rem]">{label}</p><p data-testid={id} className="mt-1 font-serif text-2xl text-amber-50">{v}</p></div>;
  return (
    <div className="lux-card space-y-4 p-5 sm:p-6" data-testid="driver-record">
      <p className="lux-eyebrow">Your record</p>
      <div className="grid grid-cols-2 gap-4">
        {stat("Refusals this month", r.this_month, "refusals-month")}{stat("Refusals all time", r.all_time, "refusals-all-time")}
        {stat("Speeding this month", s.this_month, "speeding-month")}{stat("Speeding all time", s.all_time, "speeding-all-time")}
      </div>
      {s.speeding_record && <p data-testid="speeding-record-flag" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">Speeding record on file ({s.record_months.map((k) => new Date(`${k}-15T12:00:00`).toLocaleString("en-US", { month: "long", year: "numeric" })).join(", ")})</p>}
      {s.events.slice(0, 5).map((e) => <p key={e.id} data-testid={`speeding-event-${e.id}`} className="border-t border-white/5 pt-2 text-xs text-zinc-400">{fmtDate(e.at)} · {e.message} <span className="text-zinc-600">({e.limit_source})</span></p>)}
      {r.records.slice(0, 5).map((x, i) => <p key={i} data-testid={`refusal-${i}`} className="border-t border-white/5 pt-2 text-xs text-zinc-400">{fmtDate(x.created_at)} · {x.reason} · {x.pickup || x.trip_id}</p>)}
    </div>
  );
};

export default function DriverEarnings() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/driver/earnings").then(({ data }) => setS(data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalShell>
      <p className="lux-eyebrow">Cash account · {s?.driver_share_percent ?? 75}% driver share</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Earnings</h1>
      {s && (
        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">{CARDS.map(([k, l]) => <Card key={k} label={l} value={s[k]} testId={`earnings-${k.replace("_", "-")}`} />)}</div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <History />
            <div className="min-w-0 space-y-6">
              <Withdraw summary={s} onDone={load} />
              <DriverRecord />
              <div className="lux-card p-5 sm:p-6" data-testid="withdrawals-list">
                <p className="lux-eyebrow">Withdrawals & deposits made</p>
                {s.withdrawals.length === 0 && <p className="mt-3 text-sm text-zinc-500">No withdrawals yet.</p>}
                {s.withdrawals.map((w) => (
                  <div key={w.id} data-testid={`withdrawal-${w.id}`} className="flex justify-between border-b border-white/5 py-2.5 text-sm last:border-0">
                    <div><p className={w.status === "deposited" ? "text-emerald-300" : "text-amber-300"}>{w.status === "deposited" ? "Deposited" : "Requested"}</p>
                      <p className="text-xs text-zinc-500">{fmtDate(w.deposited_at || w.requested_at)}{w.reference && ` · ref ${w.reference}`} · ••{w.bank_last4}</p></div>
                    <span className="font-mono text-amber-100">{money(w.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
